import { useEffect, useMemo, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { Link } from 'react-router';
import { paths } from '../../../utils/constants/routes';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../components/buttons/IconButton';
import DataSourceToggleIcon from '../../../components/common/DataSourceToggleIcon';
import { dataSourcesMoreAction } from '../../../components/common/dataSourcesMoreAction';
import DataSourceFloatingLegend from '../../../components/common/DataSourceFloatingLegend';
import {
  encodeProjectsParams,
  SELECTED_PROJECT_TABS,
  selectedProjectTabPageSize
} from '../../Projects/constants';
import useHistory from '../../../hooks/useHistory';
import {
  getErrorFields,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_PROVIDE_FEEDBACK_FIELDS,
  PROFILER_SEARCH_FIELDS
} from '../../../utils/constants/fields';
import { formatNumberRange, mapFieldsToState } from '../../../utils/utils';
import { useStore } from '../../../store/store';
import GeneralError from '../../../components/errors/GeneralError';
import GeneralLoader from '../../../components/loaders/GeneralLoader';
import ProfilerFeedbackSearchResultsTabView from './ProfilerFeedbackSearchResultsTabView';
import { API_ENDPOINTS } from '../../../api/endpoints';
import TextArea from '../../../components/inputs/TextArea';
import { SHARED_PROJECT_TYPE_TABS } from './constants';
import { runInAction } from 'mobx';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES
} from '../../../utils/constants/searchCategories';
import CompanyDetails from '../../../components/profiler/CompanyDetails/CompanyDetails';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import ChipsList from '../../../components/common/ChipsList';
import ResultsMobileActionBar from '../../../components/common/ResultsMobileActionBar';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { useCompanyNavigationStack } from '../../../hooks/useCompanyNavigationStack';
import {
  isBulkSearchProject,
  bulkSearchCriteriaText
} from '../../ProfilerProjects/components/bulkSearchCriteria';
import { hideBulkMatchBadgeRows } from '../../../utils/profiler/hideBulkMatchBadge';
import useProjectDownloads from '../../../hooks/useProjectDownloads';
import { isAnyProfilerDownloadInFlight } from '../../../utils/profilerDownloadHelpers';
import ProjectDownloadControls, {
  downloadStateIcon
} from '../../../components/profiler/ProjectDownload/ProjectDownloadControls';
import ProjectDownloadOverlays from '../../../components/profiler/ProjectDownload/ProjectDownloadOverlays';
import { isProfilerSearchDownloadDisabled } from '../../../utils/constants/variables';
import OwnershipCriteriaGroup from '../../../components/profiler/ownership/OwnershipCriteriaGroup';

const ProfilerFeedbackRequestView = observer(({ project, params, isFromAgentView }) => {
  const store = useStore();
  const { utilsStore, authStore, profilerProjectStore, profilerAccessStore } = store;
  const { navigate } = useHistory();
  const navStack = useCompanyNavigationStack({ scrollKey: project?.id });
  // The desktop toolbar row above the tabs; the results child portals into it.
  const [toolbarMountNode, setToolbarMountNode] = useState(null);
  const state = useLocalObservable(() => ({
    isRendered: true,
    unrender: () => (state.isRendered = false),
    isFieldsSectionCollapsed: true,
    toggleFieldsSection: () => {
      state.isFieldsSectionCollapsed = !state.isFieldsSectionCollapsed;
      if (!state.isFieldsSectionCollapsed) {
        setTimeout(() => {
          if (!state.isFieldsSectionCollapsed) {
            const mandatoryContainerDOM = document.getElementById('frv-mandatory-container');
            if (mandatoryContainerDOM) {
              window.scrollTo({
                top: mandatoryContainerDOM.offsetTop - (state.layout === 'mobile' ? 52 : 80),
                behavior: 'smooth'
              });
            }
          }
        }, 10);
      }
    },
    isLoading: false,
    loadError: false,
    results: [],
    fullResultsCount: 0,
    feedbacks: [],
    // Category comes from the caller's current `project` prop (see ProfilerProjectView).
    loadResults: (projectId = null, searchCategory = null) => {
      if (state.isLoading || !projectId) {
        return;
      }

      state.loadError = false;
      state.isLoading = true;
      store.makeRequest({
        endpoint: API_ENDPOINTS.GET_PROFILER_PROJECT_RESULTS,
        body: { projectId },
        onSuccess: (response) => {
          if (!state.isRendered) {
            return;
          }
          // Bulk rows never show the AI match badge: their score is the name matcher's similarity.
          if (isBulkSearchProject(searchCategory)) {
            hideBulkMatchBadgeRows(response.results);
          }
          const foundProjectOwner = authStore.companyUsers.find((u) => u.id === project.ownerId);
          const isProjectOwnerDemo =
            !!foundProjectOwner?.isCognitoProfilerDemoLite ||
            !!foundProjectOwner?.isCognitoProfilerDemoFull;
          state.results =
            profilerAccessStore.isProfilerDemo && isProjectOwnerDemo
              ? response.results.slice(0, selectedProjectTabPageSize)
              : response.results;
          state.fullResultsCount = response.totalCount || response.results.length;
        },
        onError: (errorMessage) => {
          if (!state.isRendered) {
            return;
          }

          state.loadError = errorMessage || 'Failed to obtain project results.';
        },
        onFinally: () => {
          if (!state.isRendered) {
            return;
          }

          state.isLoading = false;
        }
      });
    },
    fields: mapFieldsToState(PROFILER_PROVIDE_FEEDBACK_FIELDS),
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },
    get validationFields() {
      return getErrorFields(Object.values(PROFILER_PROVIDE_FEEDBACK_FIELDS), state.fields);
    },
    onChangeFeedback: (resultId, feedback) => {
      const index = state.feedbacks.findIndex((fb) => fb.resultId === resultId);
      if (index !== -1) {
        const updatedFeedbacks = [...state.feedbacks];
        updatedFeedbacks[index] = { ...updatedFeedbacks[index], feedback };
        state.feedbacks = updatedFeedbacks;
      } else {
        state.feedbacks = [...state.feedbacks, { resultId, feedback }];
      }
    },
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    }
  }));

  useEffect(() => {
    return () => state.unrender();
  }, [state]);

  useEffect(() => {
    return () => {
      if (navStack.detailsDisplayed) {
        navStack.displayDetails(null);
      }
    };
  }, [navStack]);

  useEffect(() => {
    state.loadResults(project.id, project.searchCategory);
  }, [project]);

  useEffect(() => {
    runInAction(() => {
      state.feedbacks = project.feedbacks.filter(
        (feedback) => feedback.userId === authStore.currentUserId
      );
    });
  }, [project]);

  const excludedResultsLength = state.results.filter((r) => r.isExcluded).length;

  useEffect(() => {
    if (state.isLoading) {
      return;
    }

    let shouldNavigate = false;
    const newParams = {};

    if (!Object.values(SELECTED_PROJECT_TABS).includes(params.selectedProjectTab)) {
      newParams.selectedProjectTab = SELECTED_PROJECT_TABS.RESULTS;
      shouldNavigate = true;
    }

    const list =
      params.selectedProjectTab === SELECTED_PROJECT_TABS.RESULTS
        ? state.results.filter((r) => !r.isExcluded)
        : params.selectedProjectTab === SELECTED_PROJECT_TABS.EXCLUDED
          ? state.results.filter((r) => r.isExcluded)
          : project.teamComments;
    let listPages = Math.ceil(list.length / selectedProjectTabPageSize);
    if (listPages === 0) {
      listPages = 1;
    }

    if (
      isNaN(params.selectedProjectTabPage) ||
      params.selectedProjectTabPage > listPages ||
      params.selectedProjectTabPage < 1
    ) {
      if (isNaN(params.selectedProjectTabPage) || params.selectedProjectTabPage < 1) {
        newParams.selectedProjectTabPage = 1;
      } else if (params.selectedProjectTabPage > listPages) {
        newParams.selectedProjectTabPage = listPages;
      }
      shouldNavigate = true;
    }

    if (shouldNavigate) {
      navigate(
        (isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM) +
          '?params=' +
          encodeProjectsParams({ ...params, ...newParams }),
        { replace: true }
      );
    }
  }, [params, state.results, excludedResultsLength]);

  const onBackClick = (ev) => {
    ev?.preventDefault?.();
    ev?.nativeEvent?.preventDefault?.();
    ev?.nativeEvent?.stopPropagation?.();
    const newParams = { ...params };
    newParams.selectedProjectId = null;
    newParams.selectedProjectTab = null;
    newParams.selectedProjectTabPage = null;
    navigate(
      (isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM) +
        '?params=' +
        encodeProjectsParams(newParams),
      { replace: true }
    );
  };

  const scrollOnAction = () => {
    setTimeout(() => {
      const el = document.getElementById('project-view-scroll-starting-point');
      const header = document.querySelector('header');
      if (el) {
        const headerHeight = header?.clientHeight || 0;
        window.scrollTo({ top: el.offsetTop - headerHeight, behavior: 'smooth' });
      }
    }, 100);
  };

  const selectedProjectTabs = [
    {
      name: 'Search results (' + state.results.filter((r) => !r.isExcluded).length + ')',
      onClick: () => {
        navigate(
          (isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM) +
            '?params=' +
            encodeProjectsParams({
              ...params,
              selectedProjectTab: SELECTED_PROJECT_TABS.RESULTS,
              selectedProjectTabPage: 1
            })
        );
        scrollOnAction();
      },
      isActive: params.selectedProjectTab === SELECTED_PROJECT_TABS.RESULTS
    },
    {
      name: 'Excluded results (' + state.results.filter((r) => r.isExcluded).length + ')',
      onClick: () => {
        navigate(
          (isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM) +
            '?params=' +
            encodeProjectsParams({
              ...params,
              selectedProjectTab: SELECTED_PROJECT_TABS.EXCLUDED,
              selectedProjectTabPage: 1
            })
        );
        scrollOnAction();
      },
      isActive: params.selectedProjectTab === SELECTED_PROJECT_TABS.EXCLUDED
    }
  ];

  const foundSharedUserInfo = useMemo(() => {
    return project.sharedUsers.find((user) => user.id === authStore.currentUserId);
  }, [project.sharedUsers, authStore.currentUserId]);

  const onResolveFeedback = () => {
    profilerProjectStore.resolveProjectFeedback(
      project.id,
      state.fields[PROFILER_PROVIDE_FEEDBACK_FIELDS.COMMENT.NAME],
      state.feedbacks,
      () => onBackClick()
    );
  };

  const isFeedbackButtonDisabled =
    (state.feedbacks.length === 0 &&
      state.fields[PROFILER_PROVIDE_FEEDBACK_FIELDS.COMMENT.NAME] === '') ||
    state.validationFields.invalidFields.length ||
    profilerProjectStore.isResolvingProjectFeedback;

  const readOnly = params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED;
  // TD-2970 — a feedback recipient may download the shared project on their own
  // entitlement and allowance (TD-2968). Same hook and components as the owner's
  // Projects view; the count is what this viewer sees (already demo-sliced above).
  const downloads = useProjectDownloads({
    project,
    nonExcludedResultsCount: state.results.filter((r) => !r.isExcluded).length,
    isLoadingResults: state.isLoading,
    // Recipients have no exclude action, so the row-cap tooltip points at the sharer.
    canExcludeCompanies: false
  });
  const showMobileBar = state.layout === 'mobile';
  // TD-2770 — Send-feedback rides in the results child's bottom bar (beside
  // View/Sort) whenever results are mounted; passed down as `mobilePrimaryAction`.
  // TD-2970 — Download joins it as a secondary action while the request is
  // pending, and takes over as the primary action on the resolved tab, where
  // there is nothing left to send (PO decision 2026-09-24).
  //
  // TD-2770 — descriptors rather than nodes: the bar runs in descriptor mode and
  // renders each CTA as its own button, so nothing has to carry page-scoped
  // styling through the bar's portal.
  // In-progress file icon while an export runs, as on Projects.
  const isExportInFlight = isAnyProfilerDownloadInFlight(downloads.state);
  const downloadAction = {
    key: 'download',
    label: 'Download',
    icon: downloadStateIcon({ isDownloading: isExportInFlight }) ?? ICON_BUTTON_ICONS.DOWNLOAD,
    onClick: downloads.state.toggle,
    active: downloads.state.isOpen,
    busy: isExportInFlight,
    disabled: isProfilerSearchDownloadDisabled
  };
  const mobilePrimaryAction = readOnly
    ? downloadAction
    : {
        label: 'Send feedback',
        icon: ICON_BUTTON_ICONS.SEND,
        onClick: onResolveFeedback,
        disabled: isFeedbackButtonDisabled
      };
  const mobileSecondaryActions = readOnly ? [] : [downloadAction];
  // The data-source toggle, in the More sheet on mobile (TD-2854 Part 1), as on Projects.
  const mobileMoreActions = [dataSourcesMoreAction(utilsStore)];
  // The desktop toolbar row above the tabs, in the Projects order (TD-2575):
  // Send feedback where Projects has Request feedback, then View / Sort from the results
  // child, then the trailing slot (data sources; Download joins it with TD-2970).
  // Send feedback keeps its filled primary look: it is the reviewer's main action.
  const isDesktop = state.layout === 'desktop';
  const toolbarFeedbackSlot = readOnly ? null : (
    <IconButton
      id="team-feedback-send-button-desktop"
      type={ICON_BUTTON_TYPES.BLUE}
      icon={ICON_BUTTON_ICONS.SEND}
      innerText="Send feedback"
      wrapClassName="pv-toolbar-text-button"
      onClick={onResolveFeedback}
      filled
      disabled={isFeedbackButtonDisabled}
    />
  );
  // No fixed-size wrapper: the toggle keeps the same padding as View / Sort, so the three
  // icon buttons match and the last one does not spill past the row's right edge.
  // TD-2970 — Download trails data sources, as on Projects; the anchor wrapper
  // positions the popup under the trigger (`.pv-download-anchor`,
  // project-download-popup.less). Rendered in the fallback row too, so the
  // trigger is there while results load (its entries are disabled until then).
  const toolbarTrailingSlot = (
    <>
      <DataSourceToggleIcon id="data-sources-btn-team" />
      <span className="pv-download-anchor">
        <ProjectDownloadControls
          downloads={downloads}
          idProps={{ id: 'team-feedback-download-results-button-desktop' }}
        />
      </span>
    </>
  );
  // While results load or failed to load the child is not mounted, so the parent keeps
  // Send feedback reachable in a row of its own, as the mobile fallback bar does (TD-2770).
  const isResultsChildMounted = !state.isLoading && !state.loadError;
  const foundComment =
    readOnly &&
    project.teamComments.find((comment) => comment.userId === authStore.currentUserId)?.commentText;

  if (navStack.detailsDisplayed) {
    return (
      <>
        {/* The CompanyDetails component mounts no legend of its own, so without
            this its "Show data sources" header toggle did nothing here (as on Projects,
            TD-2392). */}
        <DataSourceFloatingLegend />
        <CompanyDetails
          company={navStack.detailsDisplayed}
          displayDetails={navStack.displayDetails}
          goBackToPreviousCompany={navStack.goBackToPreviousCompany}
          companiesStackLength={navStack.companiesStack.length}
          savedTab={navStack.savedTabForCurrentCompany}
          savedComparablesId={navStack.savedComparablesIdForCurrentCompany}
          isBackNavigation={navStack.isBackNavigation}
          clearBackNavigationFlag={navStack.clearBackNavigationFlag}
          project={project}
          isFromFeedbackProject
        />
      </>
    );
  }

  return (
    <div className="feedback-details" style={showMobileBar ? { marginBottom: '20px' } : {}}>
      <Link
        to={isFromAgentView ? paths.TEAM : paths.PROFILER_TEAM}
        className="backlink"
        onClick={onBackClick}
      >
        Back to shared projects
      </Link>

      <div className="feedback-view-header">
        <div className="love">
          <div className="title">{project.name}</div>
        </div>

        {foundSharedUserInfo?.note && <div className="note">Note: {foundSharedUserInfo?.note}</div>}
        <TextArea
          field={PROFILER_PROVIDE_FEEDBACK_FIELDS.COMMENT}
          value={foundComment || state.fields[PROFILER_PROVIDE_FEEDBACK_FIELDS.COMMENT.NAME]}
          setFieldValue={state.setFieldValue}
          disabled={!!profilerProjectStore.isRequestingProjectFeedback || readOnly}
          messages={state.validationFields.messages}
          showOnSubmitErrorState={true}
          inputWrap={{ enable: true, className: 'comment' }}
          adjustHeight={{
            enable: true,
            minRows: state.layout === 'mobile' ? 4 : 2,
            maxRows: state.layout === 'mobile' ? 6 : 4
          }}
        />
      </div>

      <section>
        <div className="mandatory-container" id="frv-mandatory-container">
          <div className="mandatory-header">
            <div className="section-title">Search criteria</div>
            <IconButton
              type={ICON_BUTTON_TYPES.DEFAULT}
              icon={
                state.isFieldsSectionCollapsed
                  ? ICON_BUTTON_ICONS.CHEVRON_DOWN
                  : ICON_BUTTON_ICONS.CHEVRON_UP
              }
              innerText={state.isFieldsSectionCollapsed ? 'Expand' : 'Collapse'}
              onClick={state.toggleFieldsSection}
              iconOnRight
              withBorder={false}
            />
          </div>
          {!state.isFieldsSectionCollapsed && (
            <div className="fields">
              <div className="group">
                <div className="field">
                  <div className="label">Profile</div>
                  <div className="value">Corporate</div>
                </div>
                <div className="field">
                  <div className="label">Search category</div>
                  <div className="value">
                    {Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]).find(
                      (ntry) => ntry[1] === project.searchCategory
                    )?.[0] || project.searchCategory}
                  </div>
                </div>
                <div className="field empty"></div>
              </div>
              {project.searchCategory ===
                UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] && (
                <>
                  <div className="group-title">General</div>
                  <div className="group">
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.INDUSTRY.LABEL}</div>
                      <div className="value">
                        {project.fields[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME]}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.VALUE_CHAIN.LABEL}</div>
                      <div className="value">
                        {project.fields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME]}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.LABEL}</div>
                      <div className="value">
                        {project.fields[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.NAME]}
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.REVENUE_MIN.LABEL}</div>
                      <div className="value">
                        {formatNumberRange(
                          project.fields[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME],
                          project.fields[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME]
                        )}
                      </div>
                    </div>
                    <div className="field empty"></div>
                    <div className="field empty"></div>
                  </div>
                  <div className="group-title">
                    {PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA}
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.CAGR_MIN.LABEL}</div>
                      <div className="value">
                        {!project.fields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] &&
                        !project.fields[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME]
                          ? ''
                          : formatNumberRange(
                              project.fields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME],
                              project.fields[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME],
                              PUNCTUATION.EN_DASH,
                              true
                            )}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.CAGR_PERIOD.LABEL}</div>
                      <div className="value">
                        {!project.fields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME].length
                          ? ''
                          : utilsStore.getOptionName(
                              project.fields,
                              PROFILER_SEARCH_FIELDS.CAGR_PERIOD
                            )}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.LABEL}</div>
                      <div className="value">
                        {!project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME] &&
                        project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME]
                          ? ''
                          : formatNumberRange(
                              project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME],
                              project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME],
                              PUNCTUATION.EN_DASH,
                              true
                            )}
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.LABEL}</div>
                      <div className="value">
                        {!project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME]
                          ? ''
                          : formatNumberRange(
                              project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME],
                              project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.NAME]
                            )}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.HEADQUARTER.LABEL}</div>
                      <div className="value">
                        {!project.fields[PROFILER_SEARCH_FIELDS.HEADQUARTER.NAME].length
                          ? ''
                          : utilsStore
                              .getOptionName(project.fields, PROFILER_SEARCH_FIELDS.HEADQUARTER)
                              .join(', ')}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.SITUATION.LABEL}</div>
                      <div className="value">
                        {!project.fields[PROFILER_SEARCH_FIELDS.SITUATION.NAME].length
                          ? ''
                          : utilsStore
                              .getOptionName(project.fields, PROFILER_SEARCH_FIELDS.SITUATION)
                              .join(', ')}
                      </div>
                    </div>
                  </div>
                  <OwnershipCriteriaGroup fields={project.fields} />
                </>
              )}
              {project.searchCategory ===
                UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries'] && (
                <>
                  <div className="group-title">General</div>
                  <div className="group">
                    <div className="field full-width-desktop">
                      <div className="label">Industries</div>
                      <div className="value">
                        <ChipsList
                          list={utilsStore.getIndustryCodesDisplayNames(
                            project.fields[
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.INDUSTRY_CODES.NAME
                            ]
                          )}
                          visibleCount={state.layout === 'desktop' ? 88888 : 2}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.LABEL}
                      </div>
                      <div className="value">
                        {formatNumberRange(
                          project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.NAME],
                          project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX.NAME]
                        )}
                      </div>
                    </div>
                    <div className="field empty"></div>
                    <div className="field empty"></div>
                  </div>
                  <div className="group-title">
                    {PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA}
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.LABEL}
                      </div>
                      <div className="value">
                        {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME] &&
                        !project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.NAME]
                          ? ''
                          : formatNumberRange(
                              project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME],
                              project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.NAME],
                              PUNCTUATION.EN_DASH,
                              true
                            )}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.LABEL}
                      </div>
                      <div className="value">
                        {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.NAME]
                          .length
                          ? ''
                          : utilsStore.getOptionName(
                              project.fields,
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD
                            )}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.LABEL}
                      </div>
                      <div className="value">
                        {!project.fields[
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME
                        ] &&
                        !project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME]
                          ? ''
                          : formatNumberRange(
                              project.fields[
                                PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME
                              ],
                              project.fields[
                                PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME
                              ],
                              PUNCTUATION.EN_DASH,
                              true
                            )}
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.LABEL}
                      </div>
                      <div className="value">
                        {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.NAME]
                          ? ''
                          : formatNumberRange(
                              project.fields[
                                PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.NAME
                              ],
                              project.fields[
                                PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.NAME
                              ]
                            )}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.LABEL}
                      </div>
                      <div className="value">
                        {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.NAME]
                          .length
                          ? ''
                          : utilsStore
                              .getOptionName(
                                project.fields,
                                PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER
                              )
                              .join(', ')}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.LABEL}
                      </div>
                      <div className="value">
                        {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.NAME]
                          .length
                          ? ''
                          : utilsStore
                              .getOptionName(
                                project.fields,
                                PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION
                              )
                              .join(', ')}
                      </div>
                    </div>
                  </div>
                  <OwnershipCriteriaGroup fields={project.fields} />
                </>
              )}
              {/* Bulk uploads have no mapped criteria fields — show source file (TD-2864). */}
              {isBulkSearchProject(project.searchCategory) && (
                <div className="group">
                  <div className="field full-width-desktop">
                    <div className="value">{bulkSearchCriteriaText(project)}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      <section className="lists">
        <div id="project-view-scroll-starting-point" />
        <div id="tab-before-after-helper" />
        {/* Desktop toolbar row, sticky above the tabs (as on Projects, TD-2749).
            The portal target stays an empty element React never renders children into;
            the fallback row is a sibling that only exists while the child is absent. */}
        {isDesktop && (
          <div className="team-feedback-toolbar">
            {isResultsChildMounted ? (
              <div ref={setToolbarMountNode} className="team-feedback-toolbar-mount" />
            ) : (
              <div className="team-feedback-toolbar-row">
                {toolbarFeedbackSlot}
                {toolbarTrailingSlot}
              </div>
            )}
          </div>
        )}
        <div className={`tabs request-view${isDesktop ? ' with-toolbar' : ''}`}>
          {selectedProjectTabs.map((tab, idx) => {
            return (
              <div
                key={tab.name}
                onClick={tab.onClick}
                className={`tab ${tab.isActive ? 'active' : ''}`}
                {...(idx === 0 ? { id: 'team-project-view-tab-1' } : {})}
              >
                {tab.name}
              </div>
            );
          })}
        </div>
        {state.loadError && (
          <GeneralError
            errorMessage={state.loadError}
            actionMessage="You may want to:"
            actionButtontext="Try again"
            onActionButtonClick={() => state.loadResults(project.id, project.searchCategory)}
          />
        )}

        {state.isLoading && <GeneralLoader wrapClassName="project-results-loader" />}

        <section className="lists">
          {isResultsChildMounted && (
            <ProfilerFeedbackSearchResultsTabView
              key={project.id}
              project={project}
              results={state.results}
              feedbacks={state.feedbacks}
              forTab={params.selectedProjectTab}
              params={params}
              onChangeFeedback={state.onChangeFeedback}
              displayDetails={navStack.displayDetails}
              fullResultsCount={state.fullResultsCount}
              isFromAgentView={isFromAgentView}
              mobilePrimaryAction={mobilePrimaryAction}
              mobileMoreActions={mobileMoreActions}
              toolbarMountNode={toolbarMountNode}
              toolbarFeedbackSlot={toolbarFeedbackSlot}
              toolbarTrailingSlot={toolbarTrailingSlot}
              mobileSecondaryActions={mobileSecondaryActions}
            />
          )}
          {params.selectedProjectTab === SELECTED_PROJECT_TABS.COMMENTS && null}
        </section>
      </section>

      {/* TD-2770 — with results mounted the mobile bar is rendered by the results
          child (View/Sort + this Send-feedback as `mobilePrimaryAction`). While
          results are loading or errored the child isn't mounted, so the parent still
          shows a CTA-only bar here, preserving the Phase-1 (TD-2765) mobile access in
          those states. Mutually exclusive with the child's bar; the same descriptor
          backs both, so the disabled/onClick contract cannot drift. */}
      {showMobileBar && !isResultsChildMounted && (
        <ResultsMobileActionBar
          actions={mobileSecondaryActions}
          primary={mobilePrimaryAction}
          ariaLabel="Team feedback actions"
          breakpoint="to-1200"
        />
      )}
      {/* Owns `body.show-data-sources` and the floating legend for the toggle in
          the toolbar row / mobile More sheet. */}
      <DataSourceFloatingLegend />
      {/* TD-2970 — mobile download sheet + bulk-export confirmation + contacts
          dialog; outside the company-details early return, as on Projects. */}
      <ProjectDownloadOverlays downloads={downloads} isMobile={showMobileBar} />
    </div>
  );
});

export default ProfilerFeedbackRequestView;
